/**
 * The calls the weekly made, and whether they came out right.
 *
 * Two halves. The **ledger** at the top is the score — Brier, hit rate, and one
 * row per ICD 203 band. The **queue** below is the work: calls whose date has
 * passed, waiting for somebody to say what happened.
 *
 * A top-level page rather than an Admin tab because resolving a call is an
 * editorial act, the same argument that keeps approving the weekly open to any
 * signed-in user — and because it is recurring work, which an administrative
 * door discourages.
 */
import { api, ApiError } from "../lib/api";
import { renderHeader } from "../components/header";
import { fmtDateTime } from "../lib/datetime";

interface BandRow {
  band: string;
  label: string;
  low: number;
  high: number;
  n: number;
  correct: number;
  observed: number | null;
  verdict: string;
}

interface Calibration {
  resolved: number;
  correct: number;
  hit_rate: number | null;
  brier: number | null;
  skipped: number;
  scoreable: boolean;
  min_resolved: number;
  pending: number;
  due: number;
  bands: BandRow[];
}

interface JudgmentRow {
  id: string;
  claim: string;
  band: string;
  bandLabel: string;
  resolveBy: string;
  citations: string[];
  outcome: string;
  resolvedAt: string | null;
  reportId: string;
  reportDate: string;
  due: boolean;
}

let _calibration: Calibration | null = null;
let _rows: JudgmentRow[] = [];
let _root: HTMLElement | null = null;

const OUTCOMES: Array<[string, string, string]> = [
  ["correct", "Right", "primary"],
  ["incorrect", "Wrong", "ap-action-btn"],
  ["unresolvable", "Can't tell", "ap-action-btn"],
];

export async function renderJudgmentsPage(root: HTMLElement): Promise<void> {
  _root = root;
  root.appendChild(renderHeader());
  const page = document.createElement("div");
  page.className = "jg-page";
  page.innerHTML = `<p class="muted">Loading judgments…</p>`;
  root.appendChild(page);
  await load();
}

async function load(): Promise<void> {
  try {
    const [calibration, rows] = await Promise.all([
      api<Calibration>("/judgments/calibration"),
      api<JudgmentRow[]>("/judgments?status=all"),
    ]);
    _calibration = calibration;
    _rows = rows;
  } catch {
    _calibration = null;
    _rows = [];
  }
  render();
}

function render(): void {
  if (!_root) return;
  const page = _root.querySelector<HTMLElement>(".jg-page");
  if (!page) return;
  page.innerHTML = "";

  const heading = document.createElement("h2");
  heading.className = "jg-heading";
  heading.textContent = "Judgments";
  page.appendChild(heading);

  const intro = document.createElement("p");
  intro.className = "muted jg-intro";
  intro.textContent =
    "Every dated call the weekly has made. Resolving one records what actually " +
    "happened, which is the only way the score below ever means anything.";
  page.appendChild(intro);

  if (!_calibration) {
    const err = document.createElement("p");
    err.className = "ap-error jg-error";
    err.style.display = "block";
    err.textContent = "Could not load the judgment ledger.";
    page.appendChild(err);
    return;
  }

  page.appendChild(renderLedger(_calibration));

  const due = _rows.filter((r) => r.due);
  const pending = _rows.filter((r) => r.outcome === "pending" && !r.due);
  const resolved = _rows.filter((r) => r.outcome !== "pending");

  page.appendChild(section(`${due.length} due for resolution`, due, "Nothing is due. Calls appear here on their resolve-by date."));
  if (pending.length) {
    page.appendChild(section(`${pending.length} not yet due`, pending, ""));
  }
  if (resolved.length) {
    // Resolved calls stay visible: a resolution is a record, and the ledger a
    // score is computed from should be readable, not just its total.
    const details = document.createElement("details");
    details.className = "ap-wa-decided jg-resolved";
    const summary = document.createElement("summary");
    summary.textContent = `${resolved.length} already resolved`;
    details.appendChild(summary);
    resolved.forEach((r) => details.appendChild(card(r)));
    page.appendChild(details);
  }
}

function section(title: string, rows: JudgmentRow[], empty: string): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "jg-section";
  const h = document.createElement("h3");
  h.className = "ap-wa-proposals-title";
  h.textContent = title;
  wrap.appendChild(h);
  if (!rows.length) {
    const p = document.createElement("p");
    p.className = "muted";
    p.textContent = empty;
    wrap.appendChild(p);
    return wrap;
  }
  rows.forEach((r) => wrap.appendChild(card(r)));
  return wrap;
}

/** The score, or an honest statement that there isn't one yet. */
function renderLedger(c: Calibration): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "an-section jg-ledger";

  const title = document.createElement("h3");
  title.className = "an-section-title";
  title.textContent = "Calibration";
  wrap.appendChild(title);

  if (!c.scoreable) {
    // The same sentence the weekly prints. A Brier score over a handful of
    // calls is dominated by which handful, and saying so beats a number that
    // would be read as a track record.
    const note = document.createElement("p");
    note.className = "an-note jg-thin";
    note.textContent = c.resolved
      ? `${c.resolved} call${c.resolved === 1 ? "" : "s"} resolved. A score needs about ${c.min_resolved} before it means anything.`
      : `No calls resolved yet. ${c.pending} waiting, ${c.due} of them due.`;
    wrap.appendChild(note);
  } else {
    const cards = document.createElement("div");
    cards.className = "an-cards";
    const defs: Array<[string, string]> = [
      [String(c.brier ?? "—"), "Brier score (0 is perfect)"],
      [`${Math.round((c.hit_rate ?? 0) * 100)}%`, "Called right"],
      [String(c.resolved), "Calls resolved"],
      [String(c.due), "Due now"],
    ];
    for (const [value, label] of defs) {
      const card = document.createElement("div");
      card.className = "an-card";
      const v = document.createElement("span");
      v.className = "an-card-value";
      v.textContent = value;
      const l = document.createElement("span");
      l.className = "an-card-label";
      l.textContent = label;
      card.append(v, l);
      cards.appendChild(card);
    }
    wrap.appendChild(cards);
  }

  wrap.appendChild(bandTable(c.bands));
  if (c.skipped) {
    const note = document.createElement("p");
    note.className = "an-note";
    note.textContent = `${c.skipped} call${c.skipped === 1 ? "" : "s"} marked unresolvable and excluded from the score.`;
    wrap.appendChild(note);
  }
  return wrap;
}

/**
 * One row per band, empty ones included.
 *
 * A band that has never been used is the finding — a weekly that never says
 * "almost no chance" is not using the register it was given — so unlike every
 * other list in this app, nothing is omitted for being empty.
 */
function bandTable(bands: BandRow[]): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "an-channels jg-bands";
  for (const b of bands) {
    const row = document.createElement("div");
    // Its own layout, no longer borrowing `.an-chan-row`. That grid is four
    // tracks sized for a channel breakdown — 140px, 1fr, 44px, 36px — and the
    // last of them was holding a sentence.
    row.className = "jg-band-row";

    const label = document.createElement("span");
    label.className = "an-chan-label jg-band-label";
    label.textContent = b.label;

    const barWrap = document.createElement("span");
    barWrap.className = "an-chan-bar-wrap jg-band-bar-wrap";
    // The stated range as a track, the observed rate as the fill: the gap
    // between them is the whole point and it should be visible without
    // reading the numbers.
    const stated = document.createElement("span");
    stated.className = "jg-band-stated";
    stated.style.left = `${b.low}%`;
    stated.style.width = `${b.high - b.low}%`;
    barWrap.appendChild(stated);
    if (b.observed !== null) {
      const mark = document.createElement("span");
      mark.className = "jg-band-observed";
      mark.style.left = `${Math.round(b.observed * 100)}%`;
      barWrap.appendChild(mark);
    }

    const count = document.createElement("span");
    count.className = "an-chan-count jg-band-count";
    count.textContent = b.n ? `${b.correct}/${b.n}` : "—";

    const verdict = document.createElement("span");
    // Not `.an-chan-pct`: that is monospace and right-aligned, which is right
    // for a percentage and wrong for "happened more often than stated".
    verdict.className =
      "jg-band-verdict " +
      (b.verdict === "in range"
        ? "an-accel-flat"
        : b.verdict === "too few to say"
          ? "jg-verdict-quiet"
          : "an-accel-down");
    verdict.textContent = b.n ? b.verdict : "never used";

    row.append(label, barWrap, count, verdict);
    wrap.appendChild(row);
  }
  return wrap;
}

function card(row: JudgmentRow): HTMLElement {
  const el = document.createElement("div");
  el.className = "ap-wa-proposal jg-card";

  const head = document.createElement("div");
  head.className = "ap-wa-proposal-head";
  const badge = document.createElement("span");
  badge.className = "ap-role-badge jg-band-badge";
  badge.textContent = row.bandLabel;
  head.appendChild(badge);
  el.appendChild(head);

  const claim = document.createElement("p");
  claim.className = "ap-wa-proposal-why jg-claim";
  claim.textContent = row.claim;
  el.appendChild(claim);

  const meta = document.createElement("p");
  meta.className = "muted ap-wa-definition jg-meta";
  const state =
    row.outcome === "pending"
      ? row.due
        ? "due now"
        : "not yet due"
      : `${row.outcome}${row.resolvedAt ? ` — ${fmtDateTime(row.resolvedAt)}` : ""}`;
  meta.textContent = `By ${row.resolveBy} · from the brief of ${row.reportDate} · ${state}`;
  el.appendChild(meta);

  if (row.citations.length) {
    // The same disclosure the watch-area proposals use: the claim can be
    // checked against the corpus rather than taken on trust.
    const details = document.createElement("details");
    details.className = "ap-wa-evidence";
    const summary = document.createElement("summary");
    summary.textContent = `${row.citations.length} signal${row.citations.length === 1 ? "" : "s"} behind this`;
    details.appendChild(summary);
    for (const id of row.citations) {
      const line = document.createElement("div");
      line.className = "muted ap-wa-decided-line";
      line.textContent = id;
      details.appendChild(line);
    }
    el.appendChild(details);
  }

  const err = document.createElement("p");
  err.className = "ap-error";
  err.style.display = "none";
  err.id = `jg-err-${row.id}`;

  if (row.outcome === "pending") {
    const actions = document.createElement("div");
    actions.className = "ap-wa-proposal-actions";
    for (const [outcome, label, cls] of OUTCOMES) {
      const btn = document.createElement("button");
      btn.className = cls;
      btn.textContent = label;
      btn.addEventListener("click", () => void resolve(row, outcome, el));
      actions.appendChild(btn);
    }
    el.appendChild(actions);
  }
  el.appendChild(err);
  return el;
}

async function resolve(row: JudgmentRow, outcome: string, card: HTMLElement): Promise<void> {
  const err = card.querySelector<HTMLParagraphElement>(`#jg-err-${row.id}`);
  if (err) err.style.display = "none";
  try {
    await api(`/judgments/${encodeURIComponent(row.id)}/resolve`, {
      method: "POST",
      body: { outcome },
    });
    await load();
  } catch (e) {
    // On the card, not in an alert: a refusal here names what changed —
    // somebody else resolved it, or the brief was never approved.
    if (err) {
      err.textContent = e instanceof ApiError ? e.message : "Could not record that.";
      err.style.display = "block";
    }
  }
}
